import React from 'react';
import { TruncatedCopy } from 'common/Text/TruncatedCopy';
import { DataTableTypes } from '@utmost.co/utmost-design-system';
import { useDeepMemo } from '@beeline.com/bp-design-system';
import { EngagementJobProfile } from './jobProfiles.mutations';
import { useJobProfilesIntl } from './translations/useJobProfilesIntl';
import { JobProfileStatusChip } from './StatusChip/JobProfileStatusChip';
import { JobProfileTableActions } from './JobProfileTableActions';
import { JobProfileAssignableChip } from './StatusChip/JobProfileAssignableChip';
import { JobProfileStatusModel } from 'src/graphql/types';

export function useJobProfileCells(
  onDeleteAll: (uids: string[]) => void,
  setSelectedJobProfile: (resource: EngagementJobProfile) => void,
  showJobClassifications: boolean,
  refetch: () => void
) {
  const intl = useJobProfilesIntl();

  const columns = useDeepMemo(
    (): (DataTableTypes.Column<EngagementJobProfile & { '[menu]': string }> & { hide?: boolean })[] => [
      {
        accessor: 'code',
        Header: intl.jobReferenceId,
        defaultWidth: 200,
        ellipsisOnOverflow: false,
        Cell: ({ value }) => <TruncatedCopy textCopy={value ?? '-'} dataTest="job-profile-code" />
      },
      {
        accessor: 'displayName',
        Header: intl.title,
        defaultWidth: 150,
        Cell: ({ value }) => <span data-test="job-profile-title">{value}</span>
      },
      {
        accessor: 'status',
        Header: intl.status,
        defaultWidth: 150,
        Cell: ({ value }) => (value ? <JobProfileStatusChip status={value as JobProfileStatusModel} /> : '-')
      },
      {
        accessor: 'mappedExportJobProfile',
        Header: intl.mappedExportProfile,
        defaultWidth: 200,
        Cell: ({ value }) => <span data-test="job-profile-mapped">{value?.name ?? '-'}</span>
      },
      {
        accessor: 'assignable',
        Header: intl.assignable,
        defaultWidth: 200,
        Cell: ({ value }) => <JobProfileAssignableChip assignable={value ?? undefined} />
      },
      {
        accessor: 'jobFamily',
        Header: intl.jobFamily,
        defaultWidth: 200,
        Cell: ({ value }) => <span data-test="job-profile-job-family">{value?.name ?? '-'}</span>
      },
      {
        accessor: 'countries',
        Header: intl.country,
        defaultWidth: 200,
        minWidth: 120,
        Cell: ({ value }) => (
          <span data-test="job-profile-countries">
            {value?.length ? value?.map((country) => country?.name).join(', ') : '-'}
          </span>
        )
      },
      {
        accessor: 'classifications',
        Header: intl.linkToClassification,
        defaultWidth: 200,
        Cell: ({ value }) => (
          <span data-test="job-profile-classifications">
            {value.length ? value.map((classification) => classification.name).join(', ') : '-'}
          </span>
        )
      },
      {
        accessor: 'jobClassifications',
        Header: intl.jobClassifications,
        defaultWidth: 200,
        Cell: ({ value }) => (
          <span data-test="job-profile-job-classifications">
            {value?.map((jobClassification) => jobClassification.name).join(', ')}
          </span>
        )
      },
      {
        accessor: 'summary',
        Header: intl.summary,
        defaultWidth: 200,
        Cell: ({ value }) => <span data-test="job-profile-summary">{value ?? '-'}</span>
      },
      {
        accessor: '[menu]',
        Header: '',
        sticky: 'right',
        Cell({ row }) {
          return (
            <JobProfileTableActions
              onDeleteAll={onDeleteAll}
              setSelectedJobProfile={setSelectedJobProfile}
              jobProfile={row?.original}
              refetch={refetch}
            />
          );
        }
      }
    ],
    [intl, onDeleteAll, setSelectedJobProfile, refetch]
  );

  return columns.filter(
    (column) => !column.hide && (column.accessor !== 'jobClassifications' || showJobClassifications)
  );
}
